import { format, formatISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

import useRescheduleScheduledMessageMutation from 'components/domain/automation/scheduledMessages/useRescheduleScheduledMessageMutation';
import useModalId from 'components/modal/useModalId';
import useAppModal from 'hooks/useAppModal';
import useNotify from 'hooks/useNotify';
import { BaseErrorResponse } from 'models/_base';
import { ScheduledMessage } from 'models/automation/ScheduledMessages';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { TIMEZONES } from 'utils/constants/timezones';
import { getTimezoneOffset } from 'utils/timezones';
import { ScheduledMessagesRescheduleModalFormValues } from './ScheduledMessagesRescheduleModal.schema';

const useScheduledMessageRescheduleModalFormSubmit = ({
  scheduledMessage,
}: {
  scheduledMessage: ScheduledMessage;
}) => {
  const { t } = useTranslation();
  const modalId = useModalId();
  const { closeModal } = useAppModal();
  const { notifyError, notifySuccess } = useNotify();

  const onError = (error: BaseErrorResponse) => {
    notifyError(
      getErrorMessage({
        apiErrorCode: error.response?.data?.apiErrorCode,
        apiErrorMessage: error.response?.data?.apiErrorMessage ?? '',
        baseKey: 'pageAutomations.triggers.apiErrorCodes.',
        t,
      }),
    );
  };

  const { mutateAsync: rescheduleScheduledMessage } =
    useRescheduleScheduledMessageMutation();

  const onSubmit = async (
    formValues: ScheduledMessagesRescheduleModalFormValues,
  ) => {
    const { date, time, timezone } = formValues;

    try {
      const { offsetISO } = getTimezoneOffset({
        timezone: timezone as keyof typeof TIMEZONES,
      });
      const dateString = formatISO(date, { representation: 'date' });
      const timeString = format(time, 'HH:mm:00.000');

      await rescheduleScheduledMessage(
        {
          newFireTime: `${dateString}T${timeString}${offsetISO}`,
          scheduledMessageUid: scheduledMessage.uid,
        },
        {
          onSuccess: () => {
            closeModal(modalId);
            notifySuccess(
              t('pageAutomations.scheduledMessages.modalReschedule.success'),
            );
          },
        },
      );
    } catch (error) {
      onError(error as BaseErrorResponse);
    }
  };

  return { onSubmit };
};

export default useScheduledMessageRescheduleModalFormSubmit;
